import { useState } from "react";
import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { SpanDetailTabs, TabDefinition } from "./SpanDetailTabs";
import { RawSpanDetailView, RawSpanData } from "./RawSpanDetailView";
import { SpanEventsContent } from "./SpanEventsContent";
import { safeString } from "../../utils/strings";
import { formatUSD } from "../../utils/billing";

// Types for raw LLM message format from attributes
interface LLMMessage {
  message: {
    role: string;
    content?: string;
    tool_calls?: Array<{
      tool_call: {
        function: {
          name: string;
          arguments: string;
        };
      };
    }>;
  };
}

// Normalized LLM data structure
export interface NormalizedToolParameter {
  name: string;
  type?: string;
  description?: string;
  required?: boolean;
}

export interface NormalizedTool {
  name: string;
  description?: string;
  parameters?: NormalizedToolParameter[];
}

export interface NormalizedToolCall {
  name: string;
  arguments: string;
}

export interface NormalizedMessage {
  role: "system" | "user" | "assistant";
  content?: string;
  toolCalls?: NormalizedToolCall[];
}

export interface NormalizedLLMData {
  provider?: string;
  model?: string;
  temperature?: number;
  systemInstruction?: string;
  tools?: NormalizedTool[];
  inputMessages?: NormalizedMessage[];
  outputMessages?: NormalizedMessage[];
  tokenCounts?: {
    prompt?: number;
    completion?: number;
    total?: number;
  };
  cost?: {
    usd?: number;
    unmappableReason?: string;
    vrKeyUsed?: boolean;
  };
}

// Helper functions
const parseNumber = (value: unknown): number | undefined => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

const parseString = (value: unknown): string | undefined => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return undefined;
  return String(value);
};

const setDeepValue = (target: unknown, path: Array<string | number>, value: unknown): void => {
  if (path.length === 0 || typeof target !== "object" || target === null) return;
  let current: any = target;
  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i];
    const nextKey = path[i + 1];
    if (current[key] === undefined) {
      current[key] = typeof nextKey === "number" ? [] : {};
    }
    current = current[key];
  }
  current[path[path.length - 1]] = value;
};

const buildMessagesFromAttributes = (attributes: Record<string, unknown>, prefix: string): LLMMessage[] => {
  const messages: LLMMessage[] = [];
  Object.entries(attributes).forEach(([key, value]) => {
    if (!key.startsWith(`${prefix}.`)) return;
    const parts = key.split(".");
    if (parts.length < 3) return;
    const index = Number(parts[2]);
    if (Number.isNaN(index)) return;
    const rest = parts.slice(3).map((part) => (/^\d+$/.test(part) ? Number(part) : part));
    if (rest.length === 0) return;
    if (!messages[index]) {
      messages[index] = { message: { role: "" } };
    }
    setDeepValue(messages[index], rest, value);
  });
  return messages.filter(Boolean);
};

// Provider-specific normalizers

const normalizeGoogleLLM = (_attributes: Record<string, unknown>, invocationParams: string): Partial<NormalizedLLMData> => {
  const result: Partial<NormalizedLLMData> = {};

  // Normalize escaped characters for easier parsing
  const normalizedParams = invocationParams.replace(/\\n/g, "\n").replace(/\\'/g, "'").replace(/\\"/g, '"');

  // Extract system instruction
  const systemMatch = invocationParams.match(/"system_instruction":\s*"((?:[^"\\]|\\.)*)"/);
  if (systemMatch) {
    result.systemInstruction = systemMatch[1].replace(/\\n/g, "\n").replace(/\\"/g, '"');
  }

  // Extract tools from FunctionDeclaration format
  // Split by FunctionDeclaration to process each tool separately
  const tools: NormalizedTool[] = [];
  const funcDeclSplit = normalizedParams.split(/FunctionDeclaration\(/);

  for (let i = 1; i < funcDeclSplit.length; i++) {
    const funcDecl = funcDeclSplit[i];

    // Extract name
    const nameMatch = funcDecl.match(/name='([^']+)'/);
    if (!nameMatch) continue;

    const tool: NormalizedTool = {
      name: nameMatch[1],
    };

    // Extract description - handle both single and double quoted strings
    const descMatchSingle = funcDecl.match(/description='([^']*)'/);
    const descMatchDouble = funcDecl.match(/description="([^"]*)"/);
    const descMatch = descMatchSingle || descMatchDouble;
    if (descMatch) {
      tool.description = descMatch[1];
    }

    // Extract parameters - look for properties={...} and required=[...] within parameters=Schema(...)
    // Find the properties block
    const propsMatch = funcDecl.match(/parameters=Schema\(\s*properties=\{([\s\S]*?)\},?\s*(?:required=\[([\s\S]*?)\])?/);
    if (propsMatch) {
      const propsContent = propsMatch[1];
      const requiredContent = propsMatch[2] || "";

      // Extract required param names
      const requiredParams: string[] = [];
      const reqMatches = requiredContent.match(/'([^']+)'/g);
      if (reqMatches) {
        reqMatches.forEach((m) => requiredParams.push(m.replace(/'/g, "")));
      }

      // Extract each property: 'param_name': Schema(description='...', type=<Type.STRING>)
      const params: NormalizedToolParameter[] = [];
      const paramRegex = /'([^']+)':\s*Schema\(\s*description='([^']*)',?\s*type=<Type\.([A-Z]+)/g;
      let paramMatch;
      while ((paramMatch = paramRegex.exec(propsContent)) !== null) {
        params.push({
          name: paramMatch[1],
          description: paramMatch[2],
          type: paramMatch[3].toLowerCase(),
          required: requiredParams.includes(paramMatch[1]),
        });
      }

      if (params.length > 0) {
        tool.parameters = params;
      }
    }

    tools.push(tool);
  }

  if (tools.length > 0) {
    result.tools = tools;
  }

  return result;
};

const normalizeOpenAILLM = (attributes: Record<string, unknown>, invocationParams: string): Partial<NormalizedLLMData> => {
  const result: Partial<NormalizedLLMData> = {};

  // Extract system instruction from first message if it's a system message
  const firstMsgRole = attributes["llm.input_messages.0.message.role"];
  const firstMsgContent = attributes["llm.input_messages.0.message.content"];
  if (firstMsgRole === "system" && typeof firstMsgContent === "string") {
    result.systemInstruction = firstMsgContent;
  }

  // Extract tools from structured llm.tools.*.tool.json_schema attributes
  const tools: NormalizedTool[] = [];
  Object.entries(attributes).forEach(([key, value]) => {
    const match = key.match(/^llm\.tools\.(\d+)\.tool\.json_schema$/);
    if (match && typeof value === "string") {
      try {
        const toolSchema = JSON.parse(value);
        // OpenAI format: {type: "function", function: {name, description, parameters}}
        const func = toolSchema.function || toolSchema;
        if (func.name) {
          const tool: NormalizedTool = {
            name: func.name,
            description: func.description,
          };

          // Extract parameters from function.parameters
          const params = func.parameters;
          if (params?.properties) {
            const requiredParams = params.required || [];
            tool.parameters = Object.entries(params.properties).map(([paramName, paramDef]: [string, any]) => ({
              name: paramName,
              type: paramDef.type,
              description: paramDef.description,
              required: requiredParams.includes(paramName),
            }));
          }

          tools.push(tool);
        }
      } catch {
        // Invalid JSON, skip
      }
    }
  });
  if (tools.length > 0) {
    result.tools = tools;
  }

  // Fallback: try parsing from invocationParams if no structured tools found
  if (!result.tools) {
    try {
      const parsed = JSON.parse(invocationParams);
      if (parsed.tools && Array.isArray(parsed.tools)) {
        result.tools = parsed.tools
          .filter((t: any) => t.function?.name || t.name)
          .map((t: any) => ({
            name: t.function?.name || t.name,
            description: t.function?.description || t.description,
          }));
      }
    } catch {
      // Not valid JSON
    }
  }

  return result;
};

const normalizeAnthropicLLM = (attributes: Record<string, unknown>, invocationParams: string): Partial<NormalizedLLMData> => {
  const result: Partial<NormalizedLLMData> = {};

  // Extract tools from structured llm.tools.*.tool.json_schema attributes
  const tools: NormalizedTool[] = [];
  Object.entries(attributes).forEach(([key, value]) => {
    const match = key.match(/^llm\.tools\.(\d+)\.tool\.json_schema$/);
    if (match && typeof value === "string") {
      try {
        const toolSchema = JSON.parse(value);
        if (toolSchema.name) {
          const tool: NormalizedTool = {
            name: toolSchema.name,
            description: toolSchema.description,
          };

          // Extract parameters from input_schema
          const inputSchema = toolSchema.input_schema;
          if (inputSchema?.properties) {
            const requiredParams = inputSchema.required || [];
            tool.parameters = Object.entries(inputSchema.properties).map(([paramName, paramDef]: [string, any]) => ({
              name: paramName,
              type: paramDef.type,
              description: paramDef.description,
              required: requiredParams.includes(paramName),
            }));
          }

          tools.push(tool);
        }
      } catch {
        // Invalid JSON, skip
      }
    }
  });
  if (tools.length > 0) {
    result.tools = tools;
  }

  // Extract system instruction from invocation_parameters
  try {
    const parsed = JSON.parse(invocationParams);

    // System instruction - can be string or array of content blocks
    if (parsed.system) {
      if (typeof parsed.system === "string") {
        result.systemInstruction = parsed.system;
      } else if (Array.isArray(parsed.system)) {
        // Anthropic format: [{type: "text", text: "..."}]
        const textBlocks = parsed.system.filter((block: any) => block.type === "text" && block.text).map((block: any) => block.text);
        if (textBlocks.length > 0) {
          result.systemInstruction = textBlocks.join("\n\n");
        }
      }
    }
  } catch {
    // Not valid JSON
  }

  return result;
};

// Main normalization function
export const normalizeLLMCompletion = (attributes: Record<string, unknown>): NormalizedLLMData => {
  const result: NormalizedLLMData = {};

  // Basic info
  result.provider = parseString(attributes["llm.provider"]);
  result.model = parseString(attributes["llm.model_name"]);

  // Temperature
  const invocationParams = parseString(attributes["llm.invocation_parameters"]) || "";
  const tempMatch = invocationParams.match(/"temperature":\s*([\d.]+)/);
  if (tempMatch) {
    result.temperature = parseFloat(tempMatch[1]);
  }

  // Token counts
  const tokenTotal = parseNumber(attributes["llm.token_count.total"]);
  const tokenPrompt = parseNumber(attributes["llm.token_count.prompt"]);
  const tokenCompletion = parseNumber(attributes["llm.token_count.completion"]);
  if (tokenTotal !== undefined || tokenPrompt !== undefined || tokenCompletion !== undefined) {
    result.tokenCounts = {
      total: tokenTotal,
      prompt: tokenPrompt,
      completion: tokenCompletion,
    };
  }

  // Cost — stamped on the proxy outer span by primvoices-api TraceWorker.
  const costUsd = parseNumber(attributes["cost.usd"]);
  const costUnmappable = parseString(attributes["cost.unmappable_reason"]);
  const vrKeyUsed = attributes["voicerun.vr_key_used"];
  if (costUsd !== undefined || costUnmappable || typeof vrKeyUsed === "boolean") {
    result.cost = {
      usd: costUsd,
      unmappableReason: costUnmappable,
      vrKeyUsed: typeof vrKeyUsed === "boolean" ? vrKeyUsed : undefined,
    };
  }

  // Provider-specific normalization
  const provider = result.provider?.toLowerCase() || "";
  let providerData: Partial<NormalizedLLMData> = {};

  if (provider.includes("google") || provider.includes("gemini") || invocationParams.includes("FunctionDeclaration")) {
    providerData = normalizeGoogleLLM(attributes, invocationParams);
  } else if (provider.includes("openai") || provider.includes("gpt")) {
    providerData = normalizeOpenAILLM(attributes, invocationParams);
  } else if (provider.includes("anthropic") || provider.includes("claude")) {
    providerData = normalizeAnthropicLLM(attributes, invocationParams);
  } else {
    // Try all normalizers and use first one that returns data
    providerData = normalizeOpenAILLM(attributes, invocationParams);
    if (!providerData.systemInstruction && !providerData.tools) {
      providerData = normalizeAnthropicLLM(attributes, invocationParams);
    }
    if (!providerData.systemInstruction && !providerData.tools) {
      providerData = normalizeGoogleLLM(attributes, invocationParams);
    }
  }

  Object.assign(result, providerData);

  // Build messages from attributes (common format across providers)
  const inputMessages = buildMessagesFromAttributes(attributes, "llm.input_messages");
  const outputMessages = buildMessagesFromAttributes(attributes, "llm.output_messages");

  if (inputMessages.length > 0) {
    // Filter out system messages if we already extracted systemInstruction
    const filteredInputMessages = result.systemInstruction ? inputMessages.filter((m) => m.message.role !== "system") : inputMessages;

    if (filteredInputMessages.length > 0) {
      result.inputMessages = filteredInputMessages.map((m) => ({
        role: (m.message.role === "model" ? "assistant" : m.message.role) as "system" | "user" | "assistant",
        content: m.message.content,
        toolCalls: m.message.tool_calls?.map((tc) => ({
          name: tc.tool_call.function.name,
          arguments: tc.tool_call.function.arguments,
        })),
      }));
    }
  }

  if (outputMessages.length > 0) {
    result.outputMessages = outputMessages.map((m) => ({
      role: (m.message.role === "model" ? "assistant" : m.message.role) as "system" | "user" | "assistant",
      content: m.message.content,
      toolCalls: m.message.tool_calls?.map((tc) => ({
        name: tc.tool_call.function.name,
        arguments: tc.tool_call.function.arguments,
      })),
    }));
  }

  return result;
};

// Message bubble component for chat display
const MessageBubble = ({ role, content, toolCalls }: { role: string; content?: string; toolCalls?: NormalizedToolCall[] }) => {
  const safeRole = role || "unknown";
  const isUser = safeRole === "user";
  const isSystem = safeRole === "system";
  const isModel = safeRole === "model" || safeRole === "assistant";

  return (
    <View className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <View
        className={`max-w-[85%] rounded-lg px-3 py-2 ${
          isSystem
            ? "bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800"
            : isUser
              ? "bg-indigo-100 dark:bg-indigo-900/50 border border-indigo-200 dark:border-indigo-800"
              : "bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600"
        }`}
      >
        <View className="flex flex-row items-center gap-2 mb-1">
          <Text
            className={`text-xs font-medium ${
              isSystem ? "text-amber-700 dark:text-amber-300" : isUser ? "text-indigo-700 dark:text-indigo-300" : "text-gray-600 dark:text-gray-400"
            }`}
          >
            {isModel ? "Assistant" : safeRole.charAt(0).toUpperCase() + safeRole.slice(1)}
          </Text>
        </View>
        {content && <Text className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{safeString(content)}</Text>}
        {toolCalls && toolCalls.length > 0 && (
          <View className="mt-2 space-y-2">
            {toolCalls.map((tc, i) => (
              <View
                key={i}
                className="bg-gray-200 dark:bg-gray-600 rounded p-2"
              >
                <View className="flex flex-row items-center gap-1 mb-1">
                  <Icon
                    name="settings"
                    size={12}
                    className="text-gray-500 dark:text-gray-400"
                  />
                  <Text className="text-xs font-medium text-gray-700 dark:text-gray-300">{safeString(tc.name)}</Text>
                </View>
                <Text className="text-xs text-gray-600 dark:text-gray-400 font-mono break-all">{safeString(tc.arguments)}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    </View>
  );
};

// Check if provider is one we support for the Completion tab
export const isSupportedLLMProvider = (provider?: string): boolean => {
  if (!provider) return false;
  const p = provider.toLowerCase();
  return p.includes("openai") || p.includes("gpt") || p.includes("anthropic") || p.includes("claude") || p.includes("google") || p.includes("gemini");
};

// Check if normalized data has content to display
export const hasLLMContent = (data: NormalizedLLMData): boolean => {
  return Boolean(
    data.systemInstruction ||
      data.tools?.length ||
      data.inputMessages?.length ||
      data.outputMessages?.length ||
      data.cost?.usd !== undefined ||
      data.cost?.unmappableReason,
  );
};

// Content component for the Completion tab
const CompletionContent = ({ data }: { data: NormalizedLLMData }) => {
  return (
    <View className="space-y-4">
      {/* Cost — internal/provider cost stamped at trace ingest. */}
      {data.cost && (data.cost.usd !== undefined || data.cost.unmappableReason) && (
        <View className="mb-4">
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="credit-card"
              size={14}
              className="text-emerald-500 dark:text-emerald-400"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Cost</Text>
          </View>
          <View className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 border border-emerald-200 dark:border-emerald-800">
            {data.cost.usd !== undefined && (
              <View className="flex flex-row items-center justify-between">
                <Text className="text-sm text-gray-700 dark:text-gray-300">Internal cost</Text>
                <Text className="text-sm font-mono font-medium text-gray-900 dark:text-gray-100">{formatUSD(data.cost.usd)}</Text>
              </View>
            )}
            {data.cost.unmappableReason && (
              <Text className="text-xs text-amber-700 dark:text-amber-300 mt-1">No pricing available: {data.cost.unmappableReason}</Text>
            )}
            {data.cost.vrKeyUsed !== undefined && (
              <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {data.cost.vrKeyUsed ? "Charged to VoiceRun key" : "Charged to customer key"}
              </Text>
            )}
          </View>
        </View>
      )}

      {/* System Instruction */}
      {data.systemInstruction && (
        <View className="mb-4">
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="snippet"
              size={14}
              className="text-gray-600 dark:text-gray-300"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">System Instruction</Text>
          </View>
          <View className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800 max-h-48 overflow-y-auto">
            <Text className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{data.systemInstruction}</Text>
          </View>
        </View>
      )}

      {/* Tools */}
      {data.tools && data.tools.length > 0 && (
        <View className="mb-4">
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="tool"
              size={14}
              className="text-amber-500 dark:text-amber-400"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Tools ({data.tools.length})</Text>
          </View>
          <View className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 space-y-2">
            {data.tools.map((tool, i) => (
              <View
                key={i}
                className="bg-white dark:bg-gray-700 rounded p-2 border border-gray-200 dark:border-gray-600"
              >
                <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">{safeString(tool.name)}</Text>
                {tool.description && <Text className="text-xs text-gray-600 dark:text-gray-400 mt-1 block">{safeString(tool.description)}</Text>}
                {tool.parameters && tool.parameters.length > 0 && (
                  <View className="mt-2 pl-2 border-l-2 border-gray-200 dark:border-gray-600">
                    <Text className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Parameters:</Text>
                    {tool.parameters.map((param, j) => (
                      <View
                        key={j}
                        className="mb-1"
                      >
                        <View className="flex flex-row items-center gap-1">
                          <Text className="text-xs font-mono text-gray-700 dark:text-gray-300">{safeString(param.name)}</Text>
                          {param.type && <Text className="text-xs text-gray-500 dark:text-gray-500">({safeString(param.type)})</Text>}
                          {param.required && <Text className="text-xs text-red-500 dark:text-red-400">*</Text>}
                        </View>
                        {param.description && <Text className="text-xs text-gray-500 dark:text-gray-400 ml-2">{safeString(param.description)}</Text>}
                      </View>
                    ))}
                  </View>
                )}
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Input Messages */}
      {data.inputMessages && data.inputMessages.length > 0 && (
        <View>
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="message"
              size={14}
              className="text-indigo-500"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Input Messages</Text>
          </View>
          <View className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            {data.inputMessages.map((msg, i) => (
              <MessageBubble
                key={i}
                role={msg.role}
                content={msg.content}
                toolCalls={msg.toolCalls}
              />
            ))}
          </View>
        </View>
      )}

      {/* Output Messages */}
      {data.outputMessages && data.outputMessages.length > 0 && (
        <View>
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="send"
              size={14}
              className="text-emerald-500"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Output Messages</Text>
          </View>
          <View className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 border border-emerald-200 dark:border-emerald-800">
            {data.outputMessages.map((msg, i) => (
              <MessageBubble
                key={i}
                role={msg.role}
                content={msg.content}
                toolCalls={msg.toolCalls}
              />
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

// Main CompletionSpanDetailView component with tabs
interface CompletionSpanDetailViewProps {
  data: NormalizedLLMData;
  rawData: RawSpanData;
  className?: string;
}

export const CompletionSpanDetailView = ({ data, rawData, className }: CompletionSpanDetailViewProps) => {
  const events = rawData.events ?? [];
  const tabs: TabDefinition[] = [
    { id: "completion", label: "Completion", color: "indigo" },
    ...(events.length > 0 ? [{ id: "events", label: `Events (${events.length})`, color: "amber" as const }] : []),
    { id: "attributes", label: "Attributes", color: "gray" },
  ];
  const [activeTab, setActiveTab] = useState("completion");

  return (
    <View className={`flex flex-col overflow-hidden h-full ${className || ""}`}>
      <SpanDetailTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <View className="flex-1 overflow-y-auto p-4">
        {activeTab === "completion" && <CompletionContent data={data} />}
        {activeTab === "events" && <SpanEventsContent events={events} />}
        {activeTab === "attributes" && <RawSpanDetailView data={rawData} />}
      </View>
    </View>
  );
};
